import { Fragment, useState } from 'react';

import { BackButton } from '../../../../../../components/Clickables/Controls/BackButton/BackButton';
import { Heading } from '../../../../../../components/Heading/Heading';
import {
  ALL_WAYPOINTS_TOGGLEABLE_GROUPS,
  WaypointCategories,
  WAYPOINTS,
} from '../../../../../../components/Maps/constants/mapLayers/waypoints';
import { Modal } from '../../../../../../components/Modal/Modal';
import { Text } from '../../../../../../components/Text/Text';
import { ToggleSwitch } from '../../../../../../components/ToggleSwitch/ToggleSwitch';
import { useMapLayers } from '@domain/map/MapLayersContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { useUserInfo } from '@domain/user/useUserInfo';
import { waypoint as proModalContent } from '../../../../constants/proModalContent';
import { useExplore } from '../../../../context/ExploreContext';
import { ProMapLayerModal } from '../../../ProMapLayerModal/ProMapLayerModal';
import { MapDrawerLayout } from '../../MapDrawerLayout';
import { MapLayerSwitch } from '../MapLayersView/components/MapLayerSwitch';

export const WaypointsSettingsView = () => {
  const { isPremium } = useUserInfo();
  const { saveParamsInUrl } = useExplore();
  const {
    layers: {
      waypoints: { isWholeLayerVisible, toggleableGroups },
    },
    toggleLayer,
    toggleToggleableGroup,
    setToggleableGroups,
  } = useMapLayers();
  const [isProModalOpen, setIsProModalOpen] = useState(false);
  const waypointProOptions = {
    proOnly: true,
    proCTA: () => {
      setIsProModalOpen(true);
    },
  };

  const current = toggleableGroups.length;
  const max = ALL_WAYPOINTS_TOGGLEABLE_GROUPS.length;

  const handleOnClose = (): void => {
    if (
      isPremium &&
      ((current === 0 && isWholeLayerVisible) || (current !== 0 && !isWholeLayerVisible))
    ) {
      toggleLayer('waypoints');
    }
    saveParamsInUrl({
      drawerView: 'map-layers',
    });
  };

  const handleShowAll = (): void => {
    if (current === max) {
      setToggleableGroups('waypoints', []);
    } else {
      setToggleableGroups('waypoints', ALL_WAYPOINTS_TOGGLEABLE_GROUPS);
    }
  };

  return (
    <MapDrawerLayout
      withCloseButton={false}
      headerContent={
        <div>
          <BackButton variant="controlLakeFogBg" onClick={handleOnClose} />
          <Heading as="h1" variant="sm" className="mb-6">
            Waypoints
          </Heading>
        </div>
      }
    >
      <Text variant="sm">
        Build your own fishing map with everything you need to remember about your favorite fishing
        spots. Add waypoints in the Fishbrain app.
      </Text>
      <div className="flex items-center justify-between">
        <Text variant="sm" className="mb-0 font-semibold">
          Showing {isPremium ? current : 0} out of {max}
        </Text>
        <div className="flex items-center">
          <Text variant="sm" className="mr-2 mb-0 font-semibold">
            Show all
          </Text>
          <ToggleSwitch
            isActive={current === max}
            onClick={handleShowAll}
            proOptions={waypointProOptions}
          />
        </div>
      </div>
      {[
        { waypointCategory: WaypointCategories.FISHING },
        { waypointCategory: WaypointCategories.AMENITIES },
        { waypointCategory: WaypointCategories.NOTEWORTHY },
      ].map(({ waypointCategory }) => (
        <Fragment key={waypointCategory}>
          <Heading as="h2" variant="2xs" className="mt-6 mb-2">
            {waypointCategory}
          </Heading>
          {Object.entries(WAYPOINTS)
            .filter(([, waypoint]) => waypoint.category === waypointCategory)
            .sort(([, waypointA], [, waypointB]) => waypointA.order - waypointB.order)
            .map(([key, waypoint]) => (
              <MapLayerSwitch
                key={key}
                title={waypoint.name}
                icon={waypoint.icon}
                isActive={toggleableGroups.includes(waypoint.toggleableGroup)}
                onClick={() => {
                  toggleToggleableGroup('waypoints', waypoint.toggleableGroup);
                  trackEvent({
                    name: 'mykiss_explore_sub_layer_toggled',
                    properties: {
                      layer_type: 'waypoints',
                      state: toggleableGroups.includes(waypoint.toggleableGroup),
                    },
                  });
                }}
                proOptions={waypointProOptions}
              />
            ))}
        </Fragment>
      ))}
      {isProModalOpen && (
        <Modal
          isOpen
          onClose={() => {
            setIsProModalOpen(false);
          }}
          size="xl"
          style={{ minHeight: '580px', maxWidth: '1280px' }}
        >
          <ProMapLayerModal {...proModalContent} />
        </Modal>
      )}
    </MapDrawerLayout>
  );
};
